/**
 * Welle 117 – Mietvertrag abgleichen.
 *
 * Scan/PDF des Mietvertrags hochladen (oder das PDF aus dem Vertragsordner nehmen) → die App liest ihn und
 * vergleicht Feld für Feld mit den Stammdaten (DB 2 = Laschen der Daten.xlsm): gleich · anders · neu.
 * Übernommen wird nur, was angehakt ist — „neu" (in der DB leer) ist vorausgewählt, „anders" nicht.
 * Die gelesenen Werte lassen sich vor dem Übernehmen korrigieren (OCR liest nicht immer richtig).
 */
import React, { useMemo, useState } from "react";
import { API_BASE } from "../../api/config";

const API = `${API_BASE}/mietverwaltung/api/vertrag`;

const STATUS = {
  gleich: { text: "gleich", farbe: "#64748b", hinter: "rgba(100,116,139,.15)" },
  anders: { text: "anders", farbe: "#fbbf24", hinter: "rgba(245,158,11,.15)" },
  neu: { text: "neu", farbe: "#4ade80", hinter: "rgba(34,197,94,.15)" },
};

const zeigen = (v, art) => {
  if (v === null || v === undefined || v === "") return "–";
  if (art === "datum") return String(v).slice(0, 10).split("-").reverse().join(".");
  if (art === "zahl") return Number(v).toLocaleString("de-DE", { maximumFractionDigits: 2 });
  return String(v);
};

export default function VertragAbgleichModal({ mietvertragNr, onClose, onFertig }) {
  const [datei, setDatei] = useState(null);
  const [ablegen, setAblegen] = useState(true);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState("");
  const [ergebnis, setErgebnis] = useState(null);     // Antwort von /abgleich
  const [auswahl, setAuswahl] = useState({});         // schluessel → true
  const [werte, setWerte] = useState({});             // schluessel → (ggf. korrigierter) Wert aus dem PDF
  const [nurUnterschiede, setNurUnterschiede] = useState(true);
  const [fertig, setFertig] = useState(null);

  const lesen = async (mitDatei) => {
    setBusy(true); setFehler(""); setErgebnis(null); setFertig(null);
    try {
      const opts = { method: "POST" };
      if (mitDatei) {
        const fd = new FormData();
        fd.append("datei", datei);
        opts.body = fd;
      }
      const r = await fetch(`${API}/${encodeURIComponent(mietvertragNr)}/abgleich`, opts);
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || "Mietvertrag konnte nicht gelesen werden");
      setErgebnis(d);
      setWerte(Object.fromEntries(d.zeilen.map((z) => [z.schluessel, z.pdf])));
      setAuswahl(Object.fromEntries(d.zeilen.filter((z) => z.status === "neu").map((z) => [z.schluessel, true])));
    } catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };

  const gruppen = useMemo(() => {
    const g = {};
    (ergebnis?.zeilen || []).filter((z) => !nurUnterschiede || z.status !== "gleich")
      .forEach((z) => { (g[z.gruppe] = g[z.gruppe] || []).push(z); });
    return g;
  }, [ergebnis, nurUnterschiede]);

  const anzahlGewaehlt = Object.values(auswahl).filter(Boolean).length;

  const uebernehmen = async () => {
    const sendung = Object.fromEntries(Object.entries(auswahl).filter(([, an]) => an).map(([k]) => [k, werte[k]]));
    if (!Object.keys(sendung).length) return;
    if (!window.confirm(`${Object.keys(sendung).length} Angabe(n) aus dem Mietvertrag in die Stammdaten übernehmen?\n`
      + "Vorhandene Werte werden dabei überschrieben.")) return;
    setBusy(true); setFehler("");
    try {
      const r = await fetch(`${API}/${encodeURIComponent(mietvertragNr)}/abgleich/uebernehmen`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ werte: sendung }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || "Übernehmen fehlgeschlagen");
      let abgelegt = null;
      if (ablegen && datei && ergebnis?.datei?.quelle === "upload") {
        const fd = new FormData();
        fd.append("datei", datei);
        fd.append("typ", "mietvertrag");
        const r2 = await fetch(`${API}/${encodeURIComponent(mietvertragNr)}/dokument`, { method: "POST", body: fd });
        const d2 = await r2.json().catch(() => ({}));
        abgelegt = r2.ok ? (d2.dateiname || "abgelegt") : `nicht abgelegt: ${d2.detail || r2.status}`;
      }
      setFertig({ geaendert: d.geaendert || [], abgelegt });
      setAuswahl({});
      onFertig && onFertig();
    } catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };

  const box = { background: "#1e293b", border: "1px solid #475569", borderRadius: 12, padding: 20,
    width: "min(900px, 95vw)", maxHeight: "88vh", overflowY: "auto", color: "#e2e8f0" };
  const th = { textAlign: "left", fontSize: 11.5, color: "#94a3b8", padding: "6px 8px", borderBottom: "1px solid #475569" };
  const td = { padding: "6px 8px", borderBottom: "1px solid rgba(71,85,105,.45)", fontSize: 13, verticalAlign: "middle" };
  const eingabe = { height: 30, width: "100%", boxSizing: "border-box", border: "1px solid #475569", borderRadius: 6,
    padding: "0 8px", background: "#334155", color: "#f1f5f9", fontSize: 13 };

  return (
    <div className="me-drawer-overlay" style={{ display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1200 }}
      onClick={onClose}>
      <div style={box} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: 0 }}>Mietvertrag abgleichen — {mietvertragNr}</h3>
        <p style={{ fontSize: 13, color: "#94a3b8", margin: "8px 0 12px" }}>
          Den unterschriebenen Mietvertrag einscannen bzw. als PDF wählen. Er wird gelesen und mit den Stammdaten
          (Laschen der Daten.xlsm) verglichen. Übernommen wird nur, was du anhakst.
        </p>

        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <input type="file" accept="application/pdf,.pdf" disabled={busy}
            onChange={(e) => { setDatei(e.target.files?.[0] || null); setErgebnis(null); }} />
          <button className="me-btn me-btn-primary" disabled={busy || !datei} onClick={() => lesen(true)}>
            {busy && datei ? "liest …" : "Scan lesen & vergleichen"}</button>
          <span style={{ fontSize: 12.5, color: "#94a3b8" }}>oder</span>
          <button className="me-btn me-btn-outline" disabled={busy} onClick={() => { setDatei(null); lesen(false); }}
            title="Nimmt das unterschriebene Mietvertrags-PDF aus dem Vertragsordner in Dropbox">
            PDF aus dem Vertragsordner</button>
        </div>
        {datei && (
          <label style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: 12.5, marginTop: 8, color: "#cbd5e1" }}>
            <input type="checkbox" checked={ablegen} onChange={(e) => setAblegen(e.target.checked)} />
            Scan beim Übernehmen im Vertragsordner ablegen
          </label>
        )}
        {fehler && <p style={{ color: "#fca5a5", fontSize: 13 }}>{fehler}</p>}

        {ergebnis && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 13, marginBottom: 8 }}>
              Gelesen: <b>{ergebnis.datei?.name}</b>
              {" · "}<span style={{ color: STATUS.gleich.farbe }}>{ergebnis.anzahl.gleich} gleich</span>
              {" · "}<span style={{ color: STATUS.anders.farbe }}>{ergebnis.anzahl.anders} anders</span>
              {" · "}<span style={{ color: STATUS.neu.farbe }}>{ergebnis.anzahl.neu} neu</span>
              {!ergebnis.passt && <span style={{ color: "#fca5a5" }}> — Achtung: die Namen im PDF passen nicht zum Vertrag!</span>}
            </div>
            {(ergebnis.hinweise || []).map((h, i) => <div key={i} style={{ color: "#fbbf24", fontSize: 12.5 }}>{h}</div>)}
            <label style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: 12.5, margin: "8px 0", color: "#cbd5e1" }}>
              <input type="checkbox" checked={nurUnterschiede} onChange={(e) => setNurUnterschiede(e.target.checked)} />
              nur Unterschiede zeigen
            </label>
            {Object.keys(gruppen).length === 0 && (
              <p style={{ color: "#86efac", fontSize: 13 }}>Keine Unterschiede — Mietvertrag und Stammdaten stimmen überein.</p>
            )}
            {Object.entries(gruppen).map(([gruppe, zeilen]) => (
              <div key={gruppe} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11.5, letterSpacing: ".06em", textTransform: "uppercase", color: "#94a3b8", margin: "6px 0" }}>{gruppe}</div>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr>
                    <th style={{ ...th, width: 28 }} /><th style={th}>Feld</th><th style={th}>In den Stammdaten</th>
                    <th style={th}>Im Mietvertrag (änderbar)</th><th style={{ ...th, width: 70 }}>Status</th>
                  </tr></thead>
                  <tbody>
                    {zeilen.map((z) => {
                      const st = STATUS[z.status];
                      return (
                        <tr key={z.schluessel} style={{ background: auswahl[z.schluessel] ? "rgba(99,102,241,.12)" : undefined }}>
                          <td style={td}>
                            {z.status !== "gleich" && (
                              <input type="checkbox" checked={!!auswahl[z.schluessel]}
                                onChange={(e) => setAuswahl((a) => ({ ...a, [z.schluessel]: e.target.checked }))} />
                            )}
                          </td>
                          <td style={td}>{z.label}</td>
                          <td style={{ ...td, color: "#cbd5e1" }}>{zeigen(z.db, z.art)}</td>
                          <td style={td}>
                            {z.status === "gleich" ? zeigen(z.pdf, z.art) : (
                              <input style={eingabe} type={z.art === "datum" ? "date" : "text"}
                                value={werte[z.schluessel] ?? ""}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setWerte((w) => ({ ...w, [z.schluessel]: v }));
                                  setAuswahl((a) => ({ ...a, [z.schluessel]: true }));
                                }} />
                            )}
                          </td>
                          <td style={td}>
                            <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 999, padding: "1px 8px",
                              color: st.farbe, background: st.hinter }}>{st.text}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}

        {fertig && (
          <div style={{ color: "#86efac", fontSize: 13, margin: "8px 0" }}>
            Übernommen: {fertig.geaendert.length ? fertig.geaendert.join(" · ") : "nichts"}
            {fertig.abgelegt && <div>Scan: {fertig.abgelegt}</div>}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 12 }}>
          <button className="me-btn me-btn-outline" onClick={onClose}>Schließen</button>
          {ergebnis && (
            <button className="me-btn me-btn-primary" disabled={busy || !anzahlGewaehlt} onClick={uebernehmen}>
              {busy ? "übernimmt …" : `Ausgewählte übernehmen (${anzahlGewaehlt})`}</button>
          )}
        </div>
      </div>
    </div>
  );
}
